import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '../../dto/util/table-column';
import { TableAction } from '../../dto/util/table-action';
import { EntidadService } from '../../services/entidad.service';
import { EntidadDto } from '../../dto/entidad/entidad.dto';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TipoTramiteDTO } from '../tramites/supersalud/models/tipoTramiteDTO';
import { AlertaModalService } from '../../services/util/alerta-modal.service';

@Component({
  selector: 'app-parametrizacion-entidades',
  templateUrl: './parametrizacion-entidades.component.html',
  styleUrl: './parametrizacion-entidades.component.scss',
})
export class ParametrizacionEntidadesComponent implements OnInit {
  entidades: EntidadDto[] = [];
  tiposTramite: TipoTramiteDTO[] = [];
  columnas: TableColumn[] = [];
  acciones: TableAction[] = [];
  entidadSeleccionada!: EntidadDto;
  entidadForm!: FormGroup;
  mostrarTabla = true;
  fileLogo: File | null = null;
  fileLogoMini: File | null = null;
  // Multilenguaje
  entidadText: any = {};
  comunText: any = {};

  tramitesValidos: boolean = true;

  @ViewChild('fileInputLogo') fileInput!: ElementRef;
  @ViewChild('fileInputLogoMini') fileInputMini!: ElementRef;
  imageSrcLogo: string | null = null; // Almacena la imagen en Base64 para mostrarla
  imageSrcLogoMini: string | null = null; // Almacena la imagen en Base64 para mostrarla

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private entidadService: EntidadService,
    private alertaModalService: AlertaModalService
  ) {}

  ngOnInit(): void {
    this.translate.stream('parametrizacion-entidades').forEach(text => {
      for (const key of Object.keys(text)) {
        this.entidadText[key] = text[key];
      }
    });
    this.translate.stream('comun-label').forEach(text => {
      for (const key of Object.keys(text)) {
        this.comunText[key] = text[key];
      }
    });
    this.inicializarColumnas();
    this.buildForm();
    this.obtenerEntidades();
  }

  private obtenerEntidades() {
    this.entidadService.obtenerEntidades().subscribe(entidades => {
      this.entidades = entidades;
    });
  }

  private obtenerTiposTramitePorEntidad(idEntidad: number) {
    this.entidadService
      .obtenerTiposTramitePorEntidad(idEntidad)
      .subscribe(tiposTramite => {
        tiposTramite.forEach(item => {
          item['editando'] = false;
          item['editado'] = false;
        });
        this.tiposTramite = tiposTramite;
      });
  }

  private inicializarColumnas() {
    this.columnas = [
      {
        field: 'entidad',
        title: this.entidadText['entidad'],
        type: 'text',
        filterable: false,
        sortable: false,
        width: '38rem',
      },
      {
        field: 'logo',
        title: 'Logo',
        type: 'image',
        widthImage: '35',
        heightImage: '35',
        filterable: false,
        sortable: false,
      },
    ];
    // Columna de acciones
    this.acciones = [
      {
        type: 'button',
        icon: 'edit',
        title: this.entidadText['configurar-entidad'],
        idAccion: '1',
      },
    ];
  }

  buildForm() {
    this.entidadForm = this.fb.group({
      nombreEntidad: ['', [Validators.required, Validators.maxLength(50)]],
      acronimo: ['', [Validators.required, Validators.maxLength(10)]],
    });
  }

  async verEntidades(entidadSeleccionada: EntidadDto) {
    this.mostrarTabla = false;
    this.fileLogoMini = null;
    this.fileLogo = null;
    if (entidadSeleccionada.logoUrl?.includes('https://')) {
      this.fileLogo = await this.base64ToFile(
        entidadSeleccionada.logo ?? '',
        entidadSeleccionada.id + '_Logo.' + entidadSeleccionada.logoUrl.split('.').pop()
      );
      // Convertimos la imagen a Base64
      const reader = new FileReader();
      reader.onload = () => {
        this.imageSrcLogo = reader.result as string; // Guardamos la imagen en Base64
      };
      reader.readAsDataURL(this.fileLogo);
    }
    if (entidadSeleccionada.logoMiniUrl?.includes('https://')) {
      const urlParts = entidadSeleccionada.logoMiniUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      this.fileLogoMini = await this.base64ToFile(
        entidadSeleccionada.logoMini ?? '',
        fileName
      );
      // Convertimos la imagen a Base64
      const reader = new FileReader();
      reader.onload = () => {
        this.imageSrcLogoMini = reader.result as string; // Guardamos la imagen en Base64
      };
      reader.readAsDataURL(this.fileLogoMini);
    }
    this.entidadSeleccionada = entidadSeleccionada;
    this.entidadForm
      .get('nombreEntidad')
      ?.setValue(this.entidadSeleccionada.entidad);
    this.entidadForm
      .get('acronimo')
      ?.setValue(this.entidadSeleccionada.acronimo ?? '');
    this.obtenerTiposTramitePorEntidad(this.entidadSeleccionada.id);
  }

  async base64ToFile(
    base64String: string,
    fileName: string,
  ): Promise<File> {
    // Extraer el tipo MIME (ej. "image/jpeg") y los datos base64
    const matches = base64String.match(/^data:(.*);base64,(.*)$/);
    if (!matches || matches.length !== 3) {
      throw new Error("Base64 no válido");
    }

    const mimeType = matches[1]; // "image/jpeg"
    const base64Data = matches[2]; // El contenido en Base64

    // Decodificar Base64
    const byteCharacters = atob(base64Data);

    // Convertir los bytes en un array de enteros
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    // Crear un Uint8Array y un Blob
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Crear el objeto File
    return new File([blob], fileName, { type: mimeType });
  }

  terminarEdicionTipoTramite(index: number) {
    const tramite = this.tiposTramite[index];

    if (tramite.Nombre_Completo.trim() === '') {
      tramite.editando = true;
      this.tramitesValidos = false;
      return;
    }

    tramite.editando = false;
    tramite.editado = true;

    // Actualiza la validez general de los trámites
    this.tramitesValidos = this.tiposTramite.every(tramite => tramite.Nombre_Completo.trim() !== '');
  }

  editarTipoTramite(index: number) {
    this.tiposTramite[index].editando = true;
  }

  openFilePicker() {
    this.fileInput.nativeElement.click(); // Activa el input file oculto
  }

  openFilePickerMini() {
    this.fileInputMini.nativeElement.click(); // Activa el input file oculto
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.fileLogo = new File(
        [file],
        this.entidadSeleccionada.id + '_Logo.' + file.name.split('.').pop(),
        { type: file.type, lastModified: file.lastModified }
      );
      // Verificamos que sea una imagen
      if (!this.fileLogo.type.startsWith('image/')) {
        this.fileLogo = null;
        this.imageSrcLogo = null;
        this.alertaModalService.error(
          'Imagen no válida',
          'Por favor, selecciona una imagen válida'
        );
      } else {
        // Convertimos la imagen a Base64
        const reader = new FileReader();
        reader.onload = () => {
          this.imageSrcLogo = reader.result as string; // Guardamos la imagen en Base64
        };
        reader.readAsDataURL(this.fileLogo);
      }
    }
  }

  onFileSelectedMini(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.fileLogoMini = new File(
        [file],
        this.entidadSeleccionada.id + '_Logomini.' + file.name.split('.').pop(),
        { type: file.type, lastModified: file.lastModified }
      );
      // Verificamos que sea una imagen
      if (!this.fileLogoMini.type.startsWith('image/')) {
        this.fileLogoMini = null;
        this.imageSrcLogoMini = null;
        this.alertaModalService.error(
          'Imagen no válida',
          'Por favor, selecciona una imagen válida'
        );
      } else {
        // Convertimos la imagen a Base64
        const reader = new FileReader();
        reader.onload = () => {
          this.imageSrcLogoMini = reader.result as string; // Guardamos la imagen en Base64
        };
        reader.readAsDataURL(this.fileLogoMini);
      }
    }
  }

  async guardarModificaciones() {
    const tiposTramiteModificados = this.tiposTramite.filter(tt => tt.editado);
    if (tiposTramiteModificados && tiposTramiteModificados.length > 0) {
      this.entidadService
        .modificarTiposTramite(tiposTramiteModificados)
        .subscribe();
    }
    const entidadAModificar: EntidadDto = {
      estado: this.entidadSeleccionada.estado,
      logo: undefined,
      id: this.entidadSeleccionada.id,
      entidad: this.entidadForm.get('nombreEntidad')?.value,
      acronimo: this.entidadForm.get('acronimo')?.value,
    };
    const formData = await this.crearFormData(entidadAModificar);

    this.entidadService.modificarEntidad(formData).subscribe({
      next: () => {
        this.alertaModalService
          .exito(
            'Entidad modificada correctamente.',
            'La entidad ha sido modificada correctamente'
          )
          .then(() => {
            this.volverTabla();
          });
      },
      error: err => {
        this.alertaModalService.error(
          'Problema modificando la entidad.',
          err.error.message
        );
      },
    });
  }

  volverTabla() {
    this.mostrarTabla = true;
    this.obtenerEntidades();
  }

  private async crearFormData(entidadAModificar: EntidadDto) {
    const formData = new FormData();
    formData.append('tramite', JSON.stringify(entidadAModificar));
    formData.append('archivos', this.fileLogo as File);
    formData.append('archivos', this.fileLogoMini as File);
    return formData;
  }
}
